import { useEffect, useState } from 'react';
import { Spinner, Stack } from 'react-bootstrap';
import { FaArrowLeft } from 'react-icons/fa6';
import { useNavigate, useParams } from 'react-router-dom';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { DetailSection, LabelText } from '../../../../../components/generic';
import ConfirmationModal from '../../../../../components/modals/ConfirmationModal';
import { useToast } from '../../../../../context/ToastContext';
import {
  useDeleteNewsItemMutation,
  useGetNewsItemQuery
} from '../../../../../services/endpoints/website/newsItem';
import { NewsItem } from '../../../../../types/website.types';
import { dateToUTC } from '../../../../../utils/dateUtils';
import { getErrorMessage } from '../../../../../utils/utils';

export const NewsItemDetails = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { data, error, isLoading } = useGetNewsItemQuery(+params.id!);
  const [newsItem, setNewsItem] = useState<NewsItem>();
  const [deleteNewsItem, { isLoading: isDeleting }] = useDeleteNewsItemMutation();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (error && !isLoading) {
      addToast('Error while loading news item', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data) {
      setNewsItem(data.newsItem);
    }
  }, [data]);

  const handleDelete = async () => {
    try {
      await deleteNewsItem(+params.id!).unwrap();
      addToast('News item deleted successfully', 'success');
      navigate('/admin/website/news-items');
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  if (isLoading || !newsItem) {
    return (
      <div className="w-100 vh-100 d-inline-flex">
        <Spinner
          variant="primary"
          className="m-auto"
        />
      </div>
    );
  }

  return (
    <>
      <div
        className="d-flex justify-content-between align-items-center px-lg-5 border-bottom px-4 py-3"
        style={{ background: '#EFEFEF' }}
      >
        <Stack
          direction="horizontal"
          gap={2}
          className="ps-lg-3 ps-0"
        >
          <button
            className="btn btn-link text-info p-0"
            onClick={() => navigate('/admin/website/news-items')}
          >
            <FaArrowLeft
              size={20}
              className="d-lg-none"
            />
            <FaArrowLeft
              size={24}
              className="d-none d-lg-block"
            />
          </button>
          <h2
            className="d-lg-none text-secondary fw-semibold text-uppercase mb-0 pt-1"
            style={{ color: '#888888', fontSize: '1rem' }}
          >
            News Items List
          </h2>
          <h2 className="d-none d-lg-block text-info fw-bold mb-0">{`News Item #${newsItem?.id}`}</h2>
        </Stack>
        <Stack
          direction="horizontal"
          gap={1}
          className="d-none d-lg-flex"
        >
          <SimpleButton
            variant="primary"
            height={36}
            onClick={() => navigate(`/admin/website/news-items/${newsItem?.id}/edit`)}
          >
            Edit News Item
          </SimpleButton>
          <SimpleButton
            variant="primary"
            height={36}
            onClick={() => setShowModal(true)}
          >
            Delete News Item
          </SimpleButton>
        </Stack>
      </div>
      <div>
        <Stack
          gap={4}
          className="p-4"
        >
          <DetailSection>
            <LabelText
              label="Name:"
              text={newsItem?.name}
            />
            <LabelText
              label="Details:"
              text={newsItem?.details}
            />
            <LabelText
              label="Published On:"
              text={newsItem?.published_on}
            />
            <LabelText
              label="Created At:"
              text={
                newsItem?.created_at
                  ? dateToUTC(new Date(newsItem?.created_at), 'MM/dd/yyyy')
                  : 'N/A'
              }
            />
            <LabelText
              label="Updated At:"
              text={
                newsItem?.updated_at
                  ? dateToUTC(new Date(newsItem?.updated_at), 'MM/dd/yyyy')
                  : 'N/A'
              }
            />
            <LabelText
              label="Image File Name:"
              text={newsItem?.image_file_name}
            />
            <LabelText
              label="Image Content Type:"
              text={newsItem?.image_content_type}
            />
            <LabelText
              label="Image File Size:"
              text={newsItem?.image_file_size}
            />
            <LabelText
              label="Image Updated At:"
              text={
                newsItem?.image_updated_at
                  ? dateToUTC(new Date(newsItem?.image_updated_at), 'MM/dd/yyyy')
                  : 'N/A'
              }
            />
          </DetailSection>
        </Stack>
      </div>
      <ConfirmationModal
        show={showModal}
        title="Delete News Item"
        message="Are you sure you want to delete this news item?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowModal(false)}
        isLoading={isDeleting}
      />
    </>
  );
};
